import { useMemo, useRef, useSyncExternalStore } from 'react';

import { getChannel } from '@ayocore/platform/messaging/client/channel';
import type { PresenceEvent, PresenceStatus, OnlineUsersResponse } from '../common/presence-types';
import logger from '@ayocore/platform/common/helpers/logger';

const CHANNEL_ID = 'presence';

// ─── State ────────────────────────────────────────────────────────────────────

interface PresenceState {
  /** userId → статус (online/away) */
  onlineUsers: Map<string, PresenceStatus>;
  isLoading: boolean;
}

// ─── Controller ───────────────────────────────────────────────────────────────

/**
 * PresenceController — синглтон, хранит Set онлайн-пользователей.
 *
 * Паттерн: Controller + useSyncExternalStore (как AuthController, UsersController).
 *
 * При создании:
 * - Запрашивает снэпшот get_online_users
 * - Подписывается на user_connected / user_disconnected
 * - При реконнекте перезапрашивает снэпшот
 */
export class PresenceController {
  private static instance: PresenceController;
  private listeners = new Set<() => void>();
  private state: PresenceState = {
    onlineUsers: new Map(),
    isLoading: true,
  };

  private constructor() {
    this.init();
  }

  static getInstance(): PresenceController {
    if (!PresenceController.instance) {
      PresenceController.instance = new PresenceController();
    }
    return PresenceController.instance;
  }

  // ===== useSyncExternalStore API =====

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): PresenceState => this.state;

  private emit(): void {
    for (const l of this.listeners) l();
  }

  private replaceState(next: Partial<PresenceState>): void {
    this.state = { ...this.state, ...next };
    this.emit();
  }

  // ===== Инициализация =====

  private init(): void {
    const channel = getChannel<{ type: string }, PresenceEvent | OnlineUsersResponse>(CHANNEL_ID);

    // Запрос начального снэпшота
    this.fetchOnlineUsers(channel);

    // Подписка на события connect/disconnect/away/active
    channel.onMessage(message => {
      const event = message as PresenceEvent;
      if (!event?.type || !event?.userId) return;

      const next = new Map(this.state.onlineUsers);
      if (event.type === 'user_connected') {
        next.set(event.userId, 'online');
      } else if (event.type === 'user_disconnected') {
        next.delete(event.userId);
      } else if (event.type === 'user_away') {
        if (next.has(event.userId)) next.set(event.userId, 'away');
      } else if (event.type === 'user_active') {
        if (next.has(event.userId)) next.set(event.userId, 'online');
      }
      this.replaceState({ onlineUsers: next });
    });

    // При реконнекте — перезапросить снэпшот
    channel.onConnectionStart(() => {
      this.fetchOnlineUsers(channel);
    });

    // Отслеживание видимости вкладки → away/active
    document.addEventListener('visibilitychange', () => {
      channel.send({ type: document.hidden ? 'user_away' : 'user_active' });
    });
  }

  private fetchOnlineUsers(
    channel: ReturnType<typeof getChannel<{ type: string }, PresenceEvent | OnlineUsersResponse>>
  ): void {
    channel
      .request({ type: 'get_online_users' })
      .then(response => {
        const res = response as OnlineUsersResponse;
        if (res?.users) {
          const next = new Map<string, PresenceStatus>();
          for (const u of res.users) {
            next.set(u.userId, u.status);
          }
          this.replaceState({ onlineUsers: next, isLoading: false });
        }
      })
      .catch(err => {
        logger.error('[PresenceController] Failed to fetch online users:', err);
        this.replaceState({ isLoading: false });
      });
  }

  // ===== Публичный API =====

  /** Проверить, онлайн ли пользователь */
  isUserOnline(userId: string | number): boolean {
    return this.state.onlineUsers.has(String(userId));
  }

  /** Получить статус пользователя */
  getUserStatus(userId: string | number): PresenceStatus | null {
    return this.state.onlineUsers.get(String(userId)) ?? null;
  }
}

// ─── React-хук ────────────────────────────────────────────────────────────────

type FullState = ReturnType<typeof withActions>;

/**
 * usePresence — реактивный доступ к онлайн-статусу пользователей.
 *
 * @example
 * const { onlineUsers, isUserOnline } = usePresence();
 *
 * @example
 * const isOnline = usePresence(s => s.isUserOnline(userId));
 */
export function usePresence(): FullState;
export function usePresence<S>(selector: (full: FullState) => S): S;
export function usePresence<S = FullState>(selector?: (full: FullState) => S): S | FullState {
  const controller = PresenceController.getInstance();
  const state = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot,
    controller.getSnapshot
  );

  const full = useMemo(() => withActions(controller, state), [controller, state]);

  if (!selector) return full;

  const selectedRef = useRef<S | undefined>(undefined);
  return useMemo(() => {
    const selected = selector(full);
    if (selectedRef.current !== undefined) {
      try {
        if (JSON.stringify(selectedRef.current) === JSON.stringify(selected)) {
          return selectedRef.current;
        }
      } catch {
        /* noop */
      }
    }
    selectedRef.current = selected;
    return selected;
  }, [full, selector]);
}

function withActions(controller: PresenceController, snapshot: PresenceState) {
  return {
    // State
    onlineUsers: Array.from(snapshot.onlineUsers.keys()),
    connectionCount: snapshot.onlineUsers.size,
    isLoading: snapshot.isLoading,
    // Actions
    isUserOnline: controller.isUserOnline.bind(controller),
    getUserStatus: controller.getUserStatus.bind(controller),
  };
}
